/**
 * What every screen of a signed-in brand shares: the brand's client and words, who is signed in,
 * and the two things the frame shows on every page (what the next publish changes, and the items
 * with their badges). Screens call `refresh()` after a save or a publish so the pill, the sidebar
 * and the badges follow at once; the frame also refreshes on every navigation and when the tab
 * comes back into focus (someone else may have published meanwhile).
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { brand as brandApi, entities, products as productsApi, publishing, type BrandView, type ProductOffer, type ChangesView, type EntitySummary, type EntityType, type PublishState, type Staff } from "@/api/admin";
import type { AdminClient } from "@/api/client";
import { packFor, type Pack } from "@/vocabulary/vocabulary";

export interface Studio {
  client: AdminClient;
  pack: Pack;
  staff: Staff;
  canEdit: boolean;
  isOwner: boolean;
  changes: ChangesView | null;
  items: EntitySummary[] | null;
  brandView: BrandView | null;
  /** What the brand sells (GET /admin/v1/products): Pricing and every "Who can watch". Null while loading. */
  products: ProductOffer[] | null;
  refresh: () => Promise<void>;
  /** An item's name, from the list; undefined when it is not (or no longer) there. */
  labelOf: (type: EntityType, id: string) => string | undefined;
  stateOf: (type: EntityType, id: string) => PublishState | undefined;
  counts: Partial<Record<EntityType, number>>;
}

const StudioContext = createContext<Studio | null>(null);

export function useStudio(): Studio {
  const studio = useContext(StudioContext);
  if (!studio) throw new Error("useStudio outside StudioProvider");
  return studio;
}

export function StudioProvider({ client, staff, children }: { client: AdminClient; staff: Staff; children: ReactNode }) {
  const [changes, setChanges] = useState<ChangesView | null>(null);
  const [items, setItems] = useState<EntitySummary[] | null>(null);
  const [brandView, setBrandView] = useState<BrandView | null>(null);
  const [products, setProducts] = useState<ProductOffer[] | null>(null);

  const refresh = useCallback(async () => {
    const [c, e, b] = await Promise.allSettled([publishing.changes(client), entities.list(client), brandApi.get(client)]);
    if (c.status === "fulfilled") setChanges(c.value);
    if (e.status === "fulfilled") setItems(e.value.entities);
    if (b.status === "fulfilled") setBrandView(b.value);
  }, [client]);

  useEffect(() => {
    setChanges(null); setItems(null); setBrandView(null); setProducts(null);
    void refresh();
    // What is sold changes with a deployment, not with edits: once per brand.
    productsApi.list(client).then((r) => setProducts(r.products)).catch(() => setProducts([]));
    const again = () => void refresh();
    const onFocus = () => { if (document.visibilityState === "visible") again(); };
    window.addEventListener("hashchange", again);
    document.addEventListener("visibilitychange", onFocus);
    return () => { window.removeEventListener("hashchange", again); document.removeEventListener("visibilitychange", onFocus); };
  }, [client, refresh]);

  const value = useMemo<Studio>(() => {
    const byKey = new Map((items ?? []).map((e) => [`${e.type}:${e.id}`, e]));
    const counts: Partial<Record<EntityType, number>> = {};
    for (const e of items ?? []) counts[e.type] = (counts[e.type] ?? 0) + 1;
    return {
      client, staff, pack: packFor(client.brand.vertical),
      canEdit: staff.role !== "viewer", isOwner: staff.role === "owner",
      changes, items, brandView, products, refresh, counts,
      labelOf: (type, id) => byKey.get(`${type}:${id}`)?.label || undefined,
      stateOf: (type, id) => byKey.get(`${type}:${id}`)?.publishState,
    };
  }, [client, staff, changes, items, brandView, products, refresh]);

  return <StudioContext.Provider value={value}>{children}</StudioContext.Provider>;
}
